import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DollarSign, ShoppingCart, TrendingUp, Users,
  AlertTriangle, Package, RotateCcw, Activity,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeaders } = useAdmin();
  const [metrics, setMetrics] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, ordersRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/metrics`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/dashboard/recent-orders?limit=5`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/dashboard/top-products?limit=5`, { headers: getAuthHeaders() })
      ]);
      
      setMetrics(metricsRes.data);
      setRecentOrders(ordersRes.data.orders || []);
      setTopProducts(productsRes.data.top_products || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      pending_payment: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-stone-900">Dashboard</h1>
          <p className="text-stone-500">Welcome back! Here's what's happening with your store.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Daily Revenue */}
              <Card className="border-0 shadow-lg" data-testid="daily-revenue-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500 font-medium">Daily Revenue</p>
                      <p className="text-2xl font-black text-stone-900 mt-1">
                        {formatCurrency(metrics?.daily_revenue)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div className="flex items-center mt-3 text-sm">
                    <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                    <span className="text-green-600 font-medium">{metrics?.total_orders_today || 0} orders today</span>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Revenue */}
              <Card className="border-0 shadow-lg" data-testid="monthly-revenue-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500 font-medium">Monthly Revenue</p>
                      <p className="text-2xl font-black text-stone-900 mt-1">
                        {formatCurrency(metrics?.monthly_revenue)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <div className="flex items-center mt-3 text-sm">
                    <span className="text-stone-500">{metrics?.total_orders_month || 0} orders this month</span>
                  </div>
                </CardContent>
              </Card>

              {/* Average Order Value */}
              <Card className="border-0 shadow-lg" data-testid="aov-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500 font-medium">Avg. Order Value</p>
                      <p className="text-2xl font-black text-stone-900 mt-1">
                        {formatCurrency(metrics?.average_order_value)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex items-center mt-3 text-sm">
                    <span className="text-stone-500">{metrics?.conversion_rate || 0}% conversion</span>
                  </div>
                </CardContent>
              </Card>

              {/* Abandoned Carts */}
              <Card className="border-0 shadow-lg" data-testid="abandoned-carts-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500 font-medium">Abandoned Carts</p>
                      <p className="text-2xl font-black text-stone-900 mt-1">
                        {metrics?.abandoned_cart_count || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                  <div className="flex items-center mt-3 text-sm text-red-600">
                    <span>Potential revenue loss</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alerts Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Fraud Alerts */}
              {metrics?.fraud_alerts > 0 && (
                <Card className="border-0 shadow-lg bg-red-50 border-l-4 border-l-red-500" data-testid="fraud-alerts">
                  <CardContent className="p-4 flex items-center gap-4">
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                    <div>
                      <p className="font-bold text-red-800">{metrics.fraud_alerts} Fraud Alerts</p>
                      <p className="text-sm text-red-600">Orders need manual review</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Low Inventory */}
              {metrics?.low_inventory_alerts > 0 && (
                <Card className="border-0 shadow-lg bg-amber-50 border-l-4 border-l-amber-500" data-testid="low-inventory-alerts">
                  <CardContent className="p-4 flex items-center gap-4">
                    <Package className="h-8 w-8 text-amber-500" />
                    <div>
                      <p className="font-bold text-amber-800">{metrics.low_inventory_alerts} Low Stock</p>
                      <p className="text-sm text-amber-600">Products need restocking</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending Returns */}
              {metrics?.pending_returns > 0 && (
                <Card className="border-0 shadow-lg bg-blue-50 border-l-4 border-l-blue-500" data-testid="pending-returns">
                  <CardContent className="p-4 flex items-center gap-4">
                    <RotateCcw className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="font-bold text-blue-800">{metrics.pending_returns} Pending Returns</p>
                      <p className="text-sm text-blue-600">Returns awaiting action</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Recent Orders & Top Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Orders */}
              <Card className="border-0 shadow-lg" data-testid="recent-orders-card">
                <CardHeader className="border-b bg-stone-50">
                  <CardTitle className="text-lg font-bold">Recent Orders</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {recentOrders.length === 0 ? (
                    <div className="p-6 text-center text-stone-500">
                      No orders yet
                    </div>
                  ) : (
                    <div className="divide-y">
                      {recentOrders.map((order) => (
                        <div key={order._id} className="p-4 hover:bg-stone-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-stone-900">{order.order_number}</p>
                              <p className="text-sm text-stone-500">
                                {new Date(order.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-stone-900">{formatCurrency(order.total)}</p>
                              <span className={`text-xs px-2 py-1 rounded ${getStatusColor(order.order_status)}`}>
                                {order.order_status?.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Products */}
              <Card className="border-0 shadow-lg" data-testid="top-products-card">
                <CardHeader className="border-b bg-stone-50">
                  <CardTitle className="text-lg font-bold">Top Selling Products</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {topProducts.length === 0 ? (
                    <div className="p-6 text-center text-stone-500">
                      No sales data yet
                    </div>
                  ) : (
                    <div className="divide-y">
                      {topProducts.map((product, index) => (
                        <div key={product._id} className="p-4 hover:bg-stone-50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center font-bold text-amber-800">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-stone-900">{product.product_name}</p>
                              <p className="text-sm text-stone-500">{product.total_sold} sold</p>
                            </div>
                            <p className="font-bold text-green-600">{formatCurrency(product.total_revenue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
